<!--
====================================================================
Copyright 2024-2025 EcoNet.NZ

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
====================================================================
-->
<script setup>
import EasyEditor from './components/EasyEditor.vue'
import VersionChangelog from './components/Changelog.vue'
import Calendar from 'primevue/calendar';
import RadioButton from 'primevue/radiobutton';
// import TabPanel from 'primevue/tabpanel';
// import TabView from 'primevue/tabview';
</script>

<template>
  <VersionChangelog v-if="showChangelog" :version="appVersion" />
  <div v-else class="grid">
    <div class="col-12 md:col-6 mx-auto">
      <div class="surface-card p-4 shadow-2 border-round app-main-card">
        <div class="app-card-heading-row">
        <img
          class="app-card-logo"
          src="/favicon.png"
          width="40"
          height="40"
          alt="Easy Editor"
        />
        <div class="text-center mb-5 app-card-header">
          <!-- <div class="text-900 text-3xl font-medium mb-3">Record CAMS weed visit <a :href="iNaturalistUrl">{{ observationId }}</a></div> -->
          <div v-if="isNoParams" class="text-900 text-3xl font-medium mb-3">{{ appTitle }}</div>
          <div v-else-if="sourceConfig && provider === 'inaturalist'" class="text-900 text-3xl font-medium mb-3">
            <span>
              iNaturalist observation <a :href="sourceConfig.recordUrl" target="_blank">{{ sourceConfig.linkText }}</a>
            </span>
          </div>
          <div v-else-if="sourceConfig && provider === 'cams'" class="cams-title-block mb-3">
            <div v-if="camsEnrichedHeadline" class="text-900 text-3xl font-medium">
              {{ camsHeadlineSpecies }}
            </div>
            <div v-else class="text-900 text-3xl font-medium">
              CAMS weed record
            </div>
            <div
              v-if="camsLocationSubtitleLine"
              class="text-900 text-2xl font-medium mt-2 cams-title-location"
            >
              @ {{ camsLocationSubtitleLine }}
            </div>
            <div v-if="observationId && recordParam !== 'guid'" class="text-600 font-medium mt-2">
              CAMS instance
              <a :href="camsInstanceUrl" target="_blank" rel="noopener noreferrer">{{ observationId }}</a>
            </div>
          </div>
          <div v-else class="text-900 text-3xl font-medium mb-3">
            {{ sourceConfig ? sourceConfig.title : 'Loading...' }}
          </div>
          <span v-if="!isNoParams && provider !== 'cams'" class="text-600 font-medium">{{ headerSubtitle }}</span>
          
          <!-- Loading spinner for data fetching -->
          <div v-if="!isNoParams && isLoadingVisitData" class="mt-4 mb-4 text-center">
            <div class="loading-container">
              <div class="loading-spinner"></div>
              <div class="text-lg text-700 mt-3 font-medium">
                Loading {{ sourceConfig ? sourceConfig.displayName : 'data' }} fields...
              </div>
              <div class="text-sm text-500 mt-1">
                <span v-if="provider">Fetching previous visit data from {{ provider.toUpperCase() }}</span>
                <span v-else>Fetching previous visit data</span>
              </div>
            </div>
          </div>
          
          <!-- Error message display -->
          <div v-if="!isNoParams && hasError && !isLoadingVisitData" class="mt-4 mb-4">
            <div class="error-container">
              <div class="flex align-items-center gap-3">
                <i class="pi pi-exclamation-triangle text-red-500 text-2xl"></i>
                <div class="flex-1">
                  <div class="text-lg font-semibold text-red-700 mb-1">
                    Data Loading Failed
                  </div>
                  <div class="text-red-600 whitespace-pre-line">
                    {{ errorMessage }}
                  </div>
                </div>
              </div>
              <div class="mt-3 text-center">
                <button 
                  @click="retryDataLoad" 
                  class="retry-button px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  <i class="pi pi-refresh mr-2"></i>
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>

        <!-- Offline indicator -->
        <div v-if="!isOnline && !isNoParams" class="offline-banner mb-3">
          <i class="pi pi-wifi mr-2"></i>Offline — changes will sync when reconnected
          <span v-if="pendingCount > 0" class="pending-count ml-2">({{ pendingCount }} pending)</span>
        </div>

        <!-- Sync result message -->
        <div v-if="syncMessage" class="sync-message mb-3">{{ syncMessage }}</div>
        <!-- Per-provider pending counts when online (hidden once syncMessage shows the result) -->
        <template v-if="isOnline && !syncMessage">
          <div v-if="pendingCounts.cams > 0" class="sync-message mb-2">
            <i class="pi pi-sync mr-2"></i>
            {{ pendingCounts.cams }} CAMS visit(s) queued —
            <template v-if="providerHasSession.cams">
              <span v-if="!syncFailed.cams">syncing…</span>
              <span v-else>
                could not sync — the weed record may not have uploaded to CAMS yet.
                <Button label="Retry" size="small" severity="secondary" @click="syncFailed.cams = false; syncAllStoredSessions()" />
              </span>
            </template>
            <Button v-else label="Sync now" size="small" severity="secondary" @click="loginForProvider('cams')" />
          </div>
          <div v-if="pendingCounts.inaturalist > 0" class="sync-message mb-2">
            <i class="pi pi-sync mr-2"></i>
            {{ pendingCounts.inaturalist }} iNaturalist visit(s) queued —
            <template v-if="providerHasSession.inaturalist">
              <span v-if="!syncFailed.inaturalist">syncing…</span>
              <span v-else>
                could not sync.
                <Button label="Retry" size="small" severity="secondary" @click="syncFailed.inaturalist = false; syncAllStoredSessions()" />
              </span>
            </template>
            <Button v-else label="Sync now" size="small" severity="secondary" @click="loginForProvider('inaturalist')" />
          </div>
        </template>

        <!-- Only show form if no error AND not loading AND has params -->
        <div v-if="!isNoParams && !hasError && !isLoadingVisitData" class="flex flex-column gap-3">
          <div class="pb-4 flex align-items-center gap-2" :class="{ 'p-error': !visitDateValid }">
            <label for="visit-date" class="font-medium text-900 w-6rem">Visit Date</label>
            <Calendar id="visit-date" v-model="visitDate" dateFormat="dd/mm/yy" :maxDate="today" :showTime="true" :showSeconds="false" class="w-full" />
            <span v-if="!visitDateValid">Visit Date must be a valid date.</span>
          </div>
        </div>

        <!-- Only show form if no error AND not loading AND has params -->
        <div v-if="!isNoParams && !hasError && !isLoadingVisitData" class="flex align-items-start gap-2 mb-3">
  <label class="font-medium text-900 w-6rem mb-3 label-align">Is this weed:</label>
  <div class="inline-flex flex-wrap gap-2">
    <div class="field-radiobutton" style="padding: 0; margin: 0;">
      <RadioButton v-model="status" value="controlled" inputId="controlled" name="controlled" :disabled="disableRadioButtons" />
      <label for="controlled" class="ml-2 label-align">Controlled</label>
    </div>
    <div class="field-radiobutton" style="padding: 0; margin: 0;">
      <RadioButton v-model="status" value="alive" inputId="alive" name="alive" :disabled="disableRadioButtons" />
      <label for="alive" class="ml-2 label-align">Alive<span v-if="provider === 'inaturalist'" aria-hidden="true" class="ml-1 status-sym status-sym-red status-sym-dot"></span></label>
    </div>
    <div class="field-radiobutton" style="padding: 0; margin: 0;">
      <RadioButton v-model="status" value="dead" inputId="dead" name="dead" :disabled="disableRadioButtons" />
      <label for="dead" class="ml-2 label-align">Dead<span aria-hidden="true" class="ml-1 status-sym status-sym-green status-sym-diamond"></span></label>
    </div>
    <div class="field-radiobutton" style="padding: 0; margin: 0;">
      <RadioButton v-model="status" value="duplicate" inputId="duplicate" name="duplicate" :disabled="disableRadioButtons" />
      <label for="duplicate" class="ml-2 label-align">Duplicate<span aria-hidden="true" class="ml-1 status-sym status-sym-grey status-sym-square"></span></label>
    </div>
  </div>
</div>


        <!-- Only show EasyEditor components if no error AND not loading AND has params -->
        <EasyEditor v-if="!isNoParams && !hasError && !isLoadingVisitData && status === 'controlled'" @update-requested="disableRadioButtons = true" @edit-queued="refreshPendingCount" @auth-failed="reauthenticate" :controlled=true :observation-id="observationId" :record-param="recordParam" :code="code" :session-id="sessionId" :date-controlled="dateOnly(visitDate)" :visit-date="visitDate" :cached-visit-data="cachedVisitData" :provider="provider" :environment="environment" :is-production="isProduction" :is-authenticating="isAuthenticating"></EasyEditor>
        <EasyEditor v-if="!isNoParams && !hasError && !isLoadingVisitData && status === 'alive'" @update-requested="disableRadioButtons = true" @edit-queued="refreshPendingCount" @auth-failed="reauthenticate" :alive=true :observation-id="observationId" :record-param="recordParam" :code="code" :session-id="sessionId" :date-of-status-update="dateOnly(visitDate)" :visit-date="visitDate" :cached-visit-data="cachedVisitData" :provider="provider" :environment="environment" :is-production="isProduction" :is-authenticating="isAuthenticating"></EasyEditor>
        <EasyEditor v-if="!isNoParams && !hasError && !isLoadingVisitData && status === 'dead'" @update-requested="disableRadioButtons = true" @edit-queued="refreshPendingCount" @auth-failed="reauthenticate" :dead=true :observation-id="observationId" :record-param="recordParam" :code="code" :session-id="sessionId" :date-of-status-update="dateOnly(visitDate)" :visit-date="visitDate" :cached-visit-data="cachedVisitData" :provider="provider" :environment="environment" :is-production="isProduction" :is-authenticating="isAuthenticating"></EasyEditor>
        <EasyEditor v-if="!isNoParams && !hasError && !isLoadingVisitData && status === 'duplicate'" @update-requested="disableRadioButtons = true" @edit-queued="refreshPendingCount" @auth-failed="reauthenticate" :duplicate=true :observation-id="observationId" :record-param="recordParam" :code="code" :session-id="sessionId" :date-of-status-update="dateOnly(visitDate)" :visit-date="visitDate" :cached-visit-data="cachedVisitData" :provider="provider" :environment="environment" :is-production="isProduction" :is-authenticating="isAuthenticating"></EasyEditor>

        <!-- Offline landing page (no URL parameters) -->
        <div v-if="isNoParams" class="offline-landing">
          <div class="offline-landing-section-header">Offline Usage</div>
          <div class="offline-landing-section-header" :class="isOnline ? 'is-online' : 'is-offline'">
            {{ isOnline ? 'Currently online' : 'Currently offline' }}
          </div>
          <p class="offline-landing-message">
            You can now use CAMS {{ appTitle }} offline. When you visit a weed site, open the Easy Editor from the Field Maps app as normal — your visit data will be saved locally and synced later.
          </p>
          <div class="offline-landing-sync-status">
            <div v-if="!isOnline && pendingCount > 0" class="offline-banner-inline">
              <i class="pi pi-wifi mr-2"></i>{{ pendingCount }} visit(s) pending sync
            </div>
            <div v-else-if="isOnline && pendingCount === 0" class="sync-ok-inline">
              <i class="pi pi-check-circle mr-2"></i>All visits synced
            </div>
          </div>
          <p class="offline-landing-note">
            When coming back online, open CAMS Easy Editor from the weed map or from the app icon on your device to synchronise any weed visits that occurred while you were offline.
          </p>
        </div>

        <!-- Close button and version footer -->
        <div class="close-footer">
          <button class="close-btn" @click="closeTab">Close</button>
        </div>

        <!-- <TabView>
          <TabPanel header="Controlled">
            <div class="flex flex-column gap-3">
                <iNaturalistUpdater :controlled=true :observation-id="observationId" :code="code" :date-controlled="dateOnly(visitDate)"></iNaturalistUpdater>
            </div>
          </TabPanel>
          <TabPanel header="Alive">
            <div class="flex flex-column gap-3">
            </div>
          </TabPanel>
          <TabPanel header="Dead / Not Present">
              <iNaturalistUpdater :dead=true :observation-id="observationId" :code="code" :date-of-status-update="dateOnly(visitDate)"></iNaturalistUpdater>
          </TabPanel>
        </TabView> -->
        <div class="version-footer"><a :href="changelogUrl">Version {{ appVersion }}</a></div>
      </div>
    </div>
  </div>

  <div v-if="isDebug && !showChangelog" class="debug-panel">
    <div class="debug-title">Session Debug</div>
    <div><span class="debug-label">Provider</span> {{ provider || '—' }}</div>
    <div><span class="debug-label">Session ID</span> {{ sessionId ? sessionId.substring(0, 8) + '…' : 'none' }}</div>
    <div><span class="debug-label">Expires</span> {{ debugTimeRemaining }}</div>
    <div><span class="debug-label">Refresh token</span> {{ sessionHasRefreshToken === null ? '—' : sessionHasRefreshToken ? 'yes' : 'no' }}</div>
    <div><span class="debug-label">Environment</span> {{ environment }}</div>
  </div>
</template>

<style scoped>
.debug-panel {
  position: fixed;
  bottom: 12px;
  left: 12px;
  background: rgba(0,0,0,0.8);
  color: #fff;
  font-size: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  z-index: 9999;
  line-height: 1.8;
  max-width: 260px;
}
.debug-title { font-weight: bold; margin-bottom: 4px; }
.debug-label { opacity: 0.6; margin-right: 6px; }
</style>

<script>
const dayjs = require('dayjs')
import { saveFeature, loadFeature } from './featureCache'
import { syncAll, getAll as getAllQueued } from './offlineQueue'

function isChangelogPath() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/'
  return path === '/changelog' || path.endsWith('/changelog')
}

// id (and the legacy state alias) is an OBJECTID. guid is a GlobalID.
// When both are present, id wins.
function recordFromSearch(params) {
  const objectId = params.get('id') || params.get('state')
  if (objectId) return { observationId: objectId, recordParam: 'id' }
  const guid = params.get('guid')
  if (guid) return { observationId: guid, recordParam: 'guid' }
  return { observationId: null, recordParam: 'id' }
}

function mapAudienceFromSearch(params) {
  const map = params.get('map')
  if (map === 'public' || map === 'community' || map === 'premium') return map
  return 'community'
}

export default {
  name: 'EasyEditor',

  async created() {
    if (this.showChangelog) {
      this.isLoadingVisitData = false
      return
    }

    console.log('=== App.vue created() - Loading initial data ===')
    const params = new URLSearchParams(window.location.search)

    if (this.isDebug) {
      const s = document.createElement('script')
      s.src = 'https://cdn.jsdelivr.net/npm/eruda'
      s.onload = () => window.eruda.init()
      document.head.appendChild(s)
    }
    
    // Load environment configuration FIRST
    await this.loadEnvironmentConfig()
    
    // New clean parameter structure - normalize provider to lowercase
    this.provider = (params.get('provider') || 'cams').toLowerCase() // Default to CAMS, normalize to lowercase
    const record = recordFromSearch(params)
    this.observationId = record.observationId
    this.recordParam = record.recordParam
    this.mapAudience = mapAudienceFromSearch(params)
    this.code = params.get('code')

    // No-params mode: show offline landing page without auth or data loading
    if (this.isNoParams) {
      // Store session if we just returned from a no-params OAuth login
      const urlSession = params.get('session')
      if (urlSession) {
        const sessionProvider = this.provider
        localStorage.setItem(`easyeditor_session_${sessionProvider}`, urlSession)
        this.providerHasSession[sessionProvider] = true
        const cleanUrl = new URL(window.location)
        cleanUrl.searchParams.delete('session')
        cleanUrl.searchParams.delete('provider')
        window.history.replaceState({}, '', cleanUrl.toString())
      }
      window.addEventListener('online', () => {
        this.isOnline = true
        this.syncAllStoredSessions()
      })
      window.addEventListener('offline', () => {
        this.isOnline = false
      })
      await this.refreshPendingCount()
      this.isLoadingVisitData = false
      if (navigator.onLine) this.syncAllStoredSessions()
      return
    }

    // Configure provider UI immediately so the title renders without a "Loading..." flash
    this.configureProviderUI()

    // Parse display + pre-fill data from Field Maps URL params
    this.urlParamData = this.parseUrlParams(params)
    if (Object.keys(this.urlParamData).length > 0) {
      this.cachedVisitData = { ...this.urlParamData }
      // Show form immediately — auth will complete in the background
      this.isLoadingVisitData = false
      this.isAuthenticating = true
    }

    // Always register online/offline listeners so sync fires on reconnection
    // even if the app started offline
    window.addEventListener('online', () => {
      this.isOnline = true
      if (this.sessionId) this.runQueueSync()
    })
    window.addEventListener('offline', () => {
      this.isOnline = false
    })

    // Offline bypass — skip all auth and API calls
    if (!navigator.onLine) {
      this.configureProviderUI()
      try {
        const cached = await loadFeature(this.provider, this.observationId)
        if (cached) {
          // URL params (fresh from Field Maps) win for display fields;
          // cached data fills any last-visit fields not passed via URL
          this.cachedVisitData = { ...cached, ...this.urlParamData }
        }
      } catch (e) {
        console.warn('Could not load from feature cache:', e)
      }
      this.isLoadingVisitData = false
      this.isAuthenticating = false
      return
    }
    
    // When Field Maps provides URL params, use the stored session if available.
    // If no session exists redirect to OAuth — after auth the backend returns to
    // this observation via ?session=&provider=&id= or &guid=.
    if (Object.keys(this.urlParamData).length > 0) {
      this.sessionId = localStorage.getItem(`easyeditor_session_${this.provider}`) || null
      if (this.sessionId) {
        try {
          const sessionResponse = await fetch(`/api/session-info?session=${this.sessionId}`)
          if (!sessionResponse.ok) {
            console.log('Stored Field Maps session is invalid, clearing')
            localStorage.removeItem(`easyeditor_session_${this.provider}`)
            this.sessionId = null
          }
        } catch (error) {
          console.log('Session validation network error, keeping stored session:', error)
        }
      }
      if (!this.sessionId) {
        await this.redirectToOAuth()
        return
      }
      this.isAuthenticating = false
      this.refreshPendingCount()
      this.runQueueSync()
      return
    }

    // Handle OAuth callback OR session in URL - extract session from URL one time only
    const urlSession = params.get('session')
    if (this.code || urlSession) {
      if (urlSession) {
        localStorage.setItem(`easyeditor_session_${this.provider}`, urlSession)
        this.sessionId = urlSession
        try {
          const infoRes = await fetch(`/api/session-info?session=${urlSession}`)
          if (infoRes.ok) {
            const info = await infoRes.json()
            this.sessionExpiresAt = info.expires_at
            this.sessionHasRefreshToken = info.has_refresh_token
            console.log(`🔐 Session loaded via OAuth redirect for ${this.provider}, stored in localStorage — ${this.formatTimeRemaining(info.expires_at)} remaining`)
          } else {
            console.log(`🔐 Session loaded via OAuth redirect for ${this.provider}, stored in localStorage`)
          }
        } catch {
          console.log(`🔐 Session loaded via OAuth redirect for ${this.provider}, stored in localStorage`)
        }
        
        // Clean URL completely - remove code AND session
        const cleanUrl = new URL(window.location)
        cleanUrl.searchParams.delete('code')
        cleanUrl.searchParams.delete('session')
        window.history.replaceState({}, '', cleanUrl.toString())
        
        // Clear the code from memory since we've processed it
        this.code = null
      } else if (this.code) {
        console.error(`❌ OAuth callback received code but no session parameter`)
        this.hasError = true
        this.errorMessage = 'OAuth callback error: Session parameter missing from callback URL. Please contact systems@econet.nz'
        this.isLoadingVisitData = false
        return
      }
    }
    
    // Try to get session from localStorage if not already set
    if (!this.sessionId) {
      const storedSession = localStorage.getItem(`easyeditor_session_${this.provider}`)
      if (storedSession) {
        // Validate that the stored session is for the correct provider
        try {
          const sessionResponse = await fetch(`/api/session-info?session=${storedSession}`)
          if (sessionResponse.ok) {
            const sessionInfo = await sessionResponse.json()
            if (sessionInfo.source !== this.provider) {
              console.log(`❌ Session provider mismatch, clearing`)
              localStorage.removeItem(`easyeditor_session_${this.provider}`)
              this.sessionId = null
            } else {
              this.sessionId = storedSession
              this.sessionExpiresAt = sessionInfo.expires_at
              this.sessionHasRefreshToken = sessionInfo.has_refresh_token
              console.log(`🔄 Reusing stored ${this.provider.toUpperCase()} session — ${this.formatTimeRemaining(sessionInfo.expires_at)} remaining`)
            }
          } else {
            console.log(`❌ Stored session expired, clearing`)
            localStorage.removeItem(`easyeditor_session_${this.provider}`)
            this.sessionId = null
          }
        } catch (error) {
          // Network error — keep the stored session and let submit fail with 401 if expired
          console.log(`⚠️ Session validation network error, keeping stored session:`, error)
          this.sessionId = storedSession
        }
      }
    }
    
    // If no valid session, redirect to OAuth
    if (!this.sessionId) {
      // Prevent infinite redirect loops by checking if we just came from OAuth
      if (this.code) {
        console.error('❌ OAuth callback failed - had code but no valid session')
        this.hasError = true
        this.errorMessage = 'Authentication failed. The OAuth callback completed but the session could not be established. Please try again or contact systems@econet.nz'
        this.isLoadingVisitData = false
        return
      }
      console.log(`🔒 No valid session, redirecting to ${this.provider.toUpperCase()} OAuth`)
      if (this.isDebug) {
        console.log('⏳ Debug mode: waiting 5s before redirect…')
        await new Promise(resolve => setTimeout(resolve, 5000))
      }
      await this.redirectToOAuth()
      return
    }
    
    console.log(`🎯 Using ${this.provider.toUpperCase()} provider for record ${this.observationId}`)

    // Auth is confirmed — re-enable the submit button if it was held
    this.isAuthenticating = false
    this.refreshPendingCount()

    // If Field Maps passed URL params, use them directly — no API call needed
    if (Object.keys(this.urlParamData).length > 0) {
      this.isLoadingVisitData = false
      try {
        await saveFeature(this.provider, this.observationId, this.cachedVisitData)
      } catch (e) {
        console.warn('Could not save to feature cache:', e)
      }
    } else {
      this.loadLatestVisitData()
    }

    // Sync any queued edits from previous offline sessions
    if (this.sessionId) this.runQueueSync()
  },

  data() {
    const params = new URLSearchParams(window.location.search)
    const isDebug = params.get('debug') === '1' || sessionStorage.getItem('easyeditor_debug') === '1'
    if (isDebug) sessionStorage.setItem('easyeditor_debug', '1')
    const record = recordFromSearch(params)
    const observationId = record.observationId
    const mapAudience = mapAudienceFromSearch(params)
    const code = params.get('code')
    const isNoParams = !observationId && !code
    return {
      name: '',
      email: '',
      password: '',
      visitDate: new Date(),
      status: '',
      disableRadioButtons: false,
      isNoParams,
      observationId,
      recordParam: record.recordParam,
      mapAudience,

      today: new Date(),
      
      // Cache visit data at app level
      cachedVisitData: null,
      isLoadingVisitData: true,  // Start loading immediately on page load
      
      // Error handling
      hasError: false,
      errorMessage: '',
      
      // Session-based authentication
      sessionId: null,
      sessionExpiresAt: null,
      sessionHasRefreshToken: null,

      // Debug mode (?debug=1)
      isDebug,
      isOnline: navigator.onLine,
      isAuthenticating: false,
      urlParamData: {},
      syncMessage: '',
      pendingCounts: { cams: 0, inaturalist: 0 },
      providerHasSession: {
        cams: !!localStorage.getItem('easyeditor_session_cams'),
        inaturalist: !!localStorage.getItem('easyeditor_session_inaturalist'),
      },
      syncFailed: { cams: false, inaturalist: false },
      
      // Provider-based configuration (no auto-detection)
      provider: null, // Will be set from URL parameter in created()
      sourceConfig: null, // Will be set based on provider
      
      // Environment configuration
      environment: 'unknown',
      environmentConfig: null,
      isProduction: false,
      appVersion: process.env.VUE_APP_VERSION,
      showChangelog: isChangelogPath(),
    }
  },

  computed: {
    pendingCount() {
      return Object.values(this.pendingCounts).reduce((a, b) => a + b, 0)
    },

    appTitle() {
      if (this.environment === 'dev') return 'Easy Editor DEV'
      if (this.environment === 'test') return 'Easy Editor TEST'
      return 'Easy Editor'
    },

    visitDateValid() {
      if (!this.visitDate) {
        return false;
      }
      const date = new Date(this.visitDate);
      return !isNaN(date.getTime());
    },

    headerSubtitle() {
      if (!this.sourceConfig) {
        return 'Detecting data source...'
      }
      return this.sourceConfig.subtitle
    },

    camsSpeciesLine() {
      if (this.provider !== 'cams' || !this.cachedVisitData) return ''
      const t = this.speciesNameForDisplay(this.cachedVisitData.species_name)
      return t || ''
    },

    camsHeadlineSpecies() {
      if (this.provider !== 'cams' || !this.cachedVisitData) return ''
      const raw = this.speciesNameForDisplay(this.cachedVisitData.species_name)
      if (raw.toLowerCase() === 'other') {
        const other = String(this.cachedVisitData.other_weed_details ?? '').trim()
        if (other) return other
      }
      return raw
    },

    /** Trimmed LocationInfo for optional @ line below the main title */
    camsLocationSubtitleLine() {
      if (this.provider !== 'cams' || !this.cachedVisitData) return ''
      const s = this.cachedVisitData.locationDetails
      if (s == null) return ''
      const t = String(s).trim()
      return t || ''
    },

    camsEnrichedHeadline() {
      if (this.provider !== 'cams') return false
      return !!this.camsSpeciesLine
    },

    changelogUrl() {
      const base = process.env.BASE_URL || '/'
      return base.endsWith('/') ? `${base}changelog` : `${base}/changelog`
    },

    camsInstanceUrl() {
      const id = this.observationId
      if (id == null || String(id).trim() === '') return '#'
      const map = this.mapAudience === 'public' || this.mapAudience === 'premium'
        ? this.mapAudience
        : 'community'
      return `https://cams.econet.nz/${map}/weed/${encodeURIComponent(String(id).trim())}`
    },

    debugTimeRemaining() {
      return this.formatTimeRemaining(this.sessionExpiresAt)
    },
  },

  methods: {
    parseUrlParams(params) {
      // Reverse mappings mirror the backend cams_utils.py read path so URL params
      // (raw CAMS domain codes) are converted to EasyEditor values the same way.
      const CONTROL_METHOD_MAP = {
        'CutAndPaint': 'Cut and paint',
        'DrillAndFill': 'Drill and fill',
        'ScrapeStemAndPaste': 'Scrape stem and paste',
        'PulledOrDug': 'Pulled or dug',
        'Ringbark': 'Ringbark',
        'SprayLeaves': 'Spray leaves',
        'CutButRootsRemain': 'Cut but roots remain',
        'DontKnow': "Don't know",
      }
      const TREATMENT_SUBSTANCE_MAP = {
        '': 'None',
        'CutNPasteOriginalGel': 'CutNPaste Original Gel',
        'CutNPasteBambooBusterGel': 'CutNPaste Bamboo Buster Gel',
        'CutNPasteGlimax': 'CutNPaste Glimax',
        'CutNPasteMetGel': 'CutNPaste MetGel',
        'CutNPastePicloramGel': 'CutNPaste Picloram Gel',
        'DowVigilantII': 'Dow Vigilant II',
        'TriumphGel': 'Triumph Gel / Vigilant',
        'WeedWeaponInvadeGel': 'Weed Weapon Invade Gel',
        'WeedWeaponStumpStopGel': 'Weed Weapon Stump-Stop-Gel',
        'OTHERGEL': 'OTHER GEL',
        'GlyphosateSpray': 'Glyphosate Spray',
        'HaloxyfopSpray': 'Haloxyfop Spray',
        'MetSulfSpray': 'MetSulf Spray',
        'TriclopyrSpray': 'Triclopyr Spray',
        'OTHERLIQUIDHERBICIDE': 'OTHER LIQUID Herbicide',
        'GlyphosateGranules': 'Glyphosate Granules',
        'OTHERTREATMENT': 'OTHER-TREATMENT',
      }
      const FLOWERING_MAP = {
        '0DontKnow': 'not recorded',
        '4NoSignOfFlowering': 'vegetative only',
        '2BudsForming': 'flower buds',
        '3FlowersVisible': 'flowers',
        '7PodsForming': 'immature fruit',
        '8PodsBursting': 'mature fruit',
        '9SeedDispersed': 'seed dispersed',
      }

      const urlParamData = {}
      const directMappings = {
        species: 'species_name',
        otherWeedDetails: 'other_weed_details',
        locationDetails: 'locationDetails',
        treatmentDetails: 'treatmentDetails',
        locationDescription: 'locationDescription',
        area: 'area',
        height: 'height',
      }
      for (const [param, field] of Object.entries(directMappings)) {
        const val = params.get(param)
        if (val !== null && val !== '') urlParamData[field] = val
      }

      const controlMethodRaw = params.get('controlMethod')
      if (controlMethodRaw) {
        urlParamData.controlMethod = CONTROL_METHOD_MAP[controlMethodRaw] ?? controlMethodRaw
      }

      const treatmentSubstanceRaw = params.get('treatmentSubstance')
      if (treatmentSubstanceRaw !== null && treatmentSubstanceRaw !== '') {
        urlParamData.treatmentSubstance = TREATMENT_SUBSTANCE_MAP[treatmentSubstanceRaw] ?? treatmentSubstanceRaw
      }

      const siteDiffStr = params.get('siteDifficulty')
      if (siteDiffStr) {
        const sd = parseInt(siteDiffStr, 10)
        if (!isNaN(sd) && sd >= 1 && sd <= 5) urlParamData.siteDifficulty = sd
      }

      const phenologyRaw = params.get('phenology')
      if (phenologyRaw) {
        urlParamData.phenology = FLOWERING_MAP[phenologyRaw] ?? phenologyRaw.toLowerCase()
      }

      return urlParamData
    },

    formatTimeRemaining(expiresAt) {
      if (!expiresAt) return 'unknown'
      const secsLeft = Math.max(0, expiresAt - Date.now() / 1000)
      const h = Math.floor(secsLeft / 3600)
      const m = Math.floor((secsLeft % 3600) / 60)
      return `${h}h ${m}m`
    },
    /** Trim; drop '(' and any suffix (ArcGIS labels often include parenthetical text). */
    speciesNameForDisplay(value) {
      if (value == null) return ''
      let s = String(value).trim()
      const i = s.indexOf('(')
      if (i >= 0) s = s.slice(0, i).trim()
      return s
    },

    dateOnly(date) {
      return dayjs(date).format('YYYY-MM-DD')
    },
    
    dateTime(date) {
      return dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    
    configureProviderUI() {
      if (this.provider === 'inaturalist') {
        // iNaturalist-specific configuration
        this.sourceConfig = {
          title: `iNaturalist observation ${this.observationId}`,
          subtitle: 'as the logged-in iNaturalist user',
          displayName: 'iNaturalist',
          recordUrl: `https://inaturalist.nz/observations/${this.observationId}`,
          linkText: this.observationId
        }
      } else if (this.provider === 'cams') {
        this.sourceConfig = {
          title: `CAMS weed record ${this.observationId}`,
          subtitle: '',
          displayName: 'CAMS',
          recordUrl: '#',
          linkText: this.observationId
        }
      } else {
        // Fallback configuration
        this.sourceConfig = {
          title: `${this.provider.toUpperCase()} record ${this.observationId}`,
          subtitle: 'using EasyEditor',
          displayName: this.provider.toUpperCase(),
          recordUrl: '#',
          linkText: this.observationId
        }
      }
      
      console.log('Provider UI configured:', this.provider)
    },
    
    
    
    
    async loadLatestVisitData() {
      console.log('=== App.vue loadLatestVisitData() called ===')
      
      // Check authentication - use session if available, otherwise auth-code
      const authParam = this.sessionId ? 'session' : 'auth-code'
      const authValue = this.sessionId || this.code
      
      if (!authValue) {
        console.warn('No authentication available - redirecting to OAuth')
        // Keep spinner running during OAuth redirect, but handle errors
        try {
          await this.redirectToOAuth()
        } catch (error) {
          console.error('OAuth redirect failed:', error)
          this.isLoadingVisitData = false  // Stop spinner on OAuth error
        }
        return
      }
      
      try {
        // Use provider-specific endpoint
        const endpoint = this.provider === 'inaturalist' ? '/api/inaturalist/getLatestVisit' : '/api/cams/getLatestVisit'
        const url = new URL(endpoint, window.location.href)
        
        url.searchParams.set(authParam, authValue)
        if (this.provider === 'inaturalist') {
          url.searchParams.set('state', this.observationId || '1')
        } else {
          url.searchParams.set(this.recordParam === 'guid' ? 'guid' : 'id', this.observationId || '1')
        }
        
        console.log(`App.vue loading latest visit data from ${this.provider.toUpperCase()} using ${authParam}:`, url.toString())
        
        var response
        console.log('App.vue making fetch request...')
        response = await fetch(url)
        console.log('App.vue fetch response received:', response.status, response.statusText)
        
        // Handle authentication failures by redirecting to OAuth
        if (response.status === 401) {
          console.log('Authentication failed - clearing stored session and redirecting to OAuth')
          // Clear invalid session from localStorage
          localStorage.removeItem(`easyeditor_session_${this.provider}`)
          this.sessionId = null
          // Keep spinner running during OAuth redirect, but handle errors
          try {
            await this.redirectToOAuth()
          } catch (error) {
            console.error('OAuth redirect failed:', error)
            this.isLoadingVisitData = false  // Stop spinner on OAuth error
          }
          return
        }
        
        if (!response.ok) {
          console.log('App.vue response not OK:', response.status, response.statusText)
          
          // Try to get detailed error message from response body
          let errorDetail = ''
          try {
            const errorText = await response.text()
            errorDetail = errorText || response.statusText
            console.error('API error response:', errorDetail)
          } catch (e) {
            errorDetail = response.statusText
          }
          
          // Handle specific error cases
          if (response.status === 404) {
            this.hasError = true
            this.errorMessage = `${this.provider.toUpperCase()} record ${this.observationId} not found. Please check the record ID and try again.`
          } else if (response.status === 500) {
            this.hasError = true
            this.errorMessage = `Error retrieving ${this.provider.toUpperCase()} data: ${errorDetail}\n\nPlease report this error to systems@econet.nz`
          } else {
            this.hasError = true
            this.errorMessage = `Unable to load ${this.provider.toUpperCase()} record ${this.observationId}. Status: ${response.status}\n\nError: ${errorDetail}\n\nPlease report this to systems@econet.nz`
          }
          return
        }
        
        const visitData = await response.json()
        console.log('App.vue latest visit data loaded:', visitData)
        
        // Cache all visit data at app level
        // API data takes precedence over URL params for overlapping fields
        this.cachedVisitData = { ...this.urlParamData, ...visitData }
        try {
          await saveFeature(this.provider, this.observationId, this.cachedVisitData)
        } catch (e) {
          console.warn('Could not save to feature cache:', e)
        }
        console.log('App.vue cached visit data:', this.cachedVisitData)
        
      } catch (error) {
        console.error('App.vue error in loadLatestVisitData():', error)
        // Show error to user for network/API failures
        this.hasError = true
        this.errorMessage = `Network error loading ${this.provider.toUpperCase()} data. Please check your connection and try again.`
      } finally {
        // Always stop loading spinner, regardless of success or failure
        this.isLoadingVisitData = false
        console.log('App.vue data loading completed - spinner stopped')
      }
    },
    
    async reauthenticate() {
      localStorage.removeItem(`easyeditor_session_${this.provider}`)
      this.sessionId = null
      this.isAuthenticating = true
      await this.redirectToOAuth()
    },

    async redirectToOAuth() {
      console.log(`Redirecting to ${this.provider.toUpperCase()} OAuth for authentication`)
      
      // Prevent multiple simultaneous OAuth redirects
      if (window.oauthRedirectInProgress) {
        console.warn('OAuth redirect already in progress, skipping duplicate redirect')
        return
      }
      window.oauthRedirectInProgress = true
      
      try {
        // Get provider-specific configuration from API
        const configResponse = await fetch('/api/config')
        if (!configResponse.ok) {
          throw new Error(`Failed to fetch config: ${configResponse.status}`)
        }
        const apiConfig = await configResponse.json()
        
        // Get provider-specific config
        const providerConfig = apiConfig.providers[this.provider]
        if (!providerConfig) {
          throw new Error(`No configuration found for provider: ${this.provider}`)
        }
        
        // Get provider-specific client ID
        const clientId = this.provider === 'inaturalist' 
          ? apiConfig.inaturalist_client_id 
          : apiConfig.client_id
        
        if (!clientId || clientId.trim() === '') {
          throw new Error(`${this.provider} client ID not configured`)
        }
        
        // Generate secure CSRF state parameter
        const recordId = this.observationId || ''
        const paramName = this.provider === 'cams' && this.recordParam === 'guid' ? 'guid' : 'id'
        const mapQuery = this.provider === 'cams' ? `&map=${encodeURIComponent(this.mapAudience || 'community')}` : ''
        const stateResponse = await fetch(`/api/generate-state?provider=${encodeURIComponent(this.provider)}&${paramName}=${encodeURIComponent(recordId)}${mapQuery}`)
        
        if (!stateResponse.ok) {
          throw new Error(`Failed to generate secure state: ${stateResponse.status}`)
        }
        
        const stateData = await stateResponse.json()
        const secureState = stateData.state
        
        const redirectUri = encodeURIComponent(providerConfig.redirect_uri)
        const expiration = this.provider === 'cams' ? '&expiration=40320' : ''
        const oauthUrl = `${providerConfig.oauth_url}?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&state=${encodeURIComponent(secureState)}${expiration}`
        
        console.log(`${this.provider.toUpperCase()} OAuth URL:`, oauthUrl)
        
        // Redirect to appropriate OAuth provider
        window.location.href = oauthUrl
        
      } catch (error) {
        console.error('Error building OAuth URL:', error)
        
        // Show more specific error message
        let errorMsg = `${this.provider.toUpperCase()} authentication setup required.`
        if (error.message && error.message.includes('client ID not configured')) {
          errorMsg = `${this.provider.toUpperCase()} OAuth is not configured on this server.\n\nThe INATURALIST_CLIENT_ID environment variable is missing or empty.\n\nPlease contact systems@econet.nz to configure iNaturalist authentication.`
        } else {
          errorMsg += `\n\nError: ${error.message}\n\nPlease contact systems@econet.nz`
        }
        
        alert(errorMsg)
        // Stop spinner if OAuth setup fails
        this.isLoadingVisitData = false
        throw error  // Re-throw for calling function
      }
    },
    
    retryDataLoad() {
      console.log('Retrying data load...')
      // Reset error state
      this.hasError = false
      this.errorMessage = ''
      // Restart loading
      this.isLoadingVisitData = true
      // Retry the data load
      this.loadLatestVisitData()
    },

    closeTab() {
      window.close()
    },

    async refreshPendingCount() {
      try {
        const all = await getAllQueued()
        this.pendingCounts = {
          cams: all.filter(i => i.provider === 'cams').length,
          inaturalist: all.filter(i => i.provider === 'inaturalist').length,
        }
      } catch (e) {
        console.warn('Could not read pending queue count:', e)
      }
    },

    async runQueueSync() {
      const result = await syncAll(this.provider, this.sessionId)
      if (result.authRequired) {
        await this.redirectToOAuth()
        return
      }
      await this.refreshPendingCount()
      if (result.failed.length > 0) {
        this.syncFailed[this.provider] = true
      } else {
        this.syncFailed[this.provider] = false
        if (result.synced.length > 0) {
          this.syncMessage = `${result.synced.length} visit(s) synced.`
          setTimeout(() => { this.syncMessage = '' }, 5000)
        }
      }
    },

    // Sync queued items for providers that have items AND a stored session.
    // Providers with queued items but no session are skipped — the user only
    // needs to log in to the provider(s) they actually have records for.
    async loginForProvider(provider) {
      this.provider = provider
      this.observationId = null
      await this.redirectToOAuth()
    },

    async syncAllStoredSessions() {
      const providers = ['cams', 'inaturalist']
      let totalSynced = 0
      for (const provider of providers) {
        if (!this.pendingCounts[provider]) continue
        const sessionId = localStorage.getItem(`easyeditor_session_${provider}`)
        if (!sessionId) {
          this.providerHasSession[provider] = false
          continue
        }
        this.providerHasSession[provider] = true
        const result = await syncAll(provider, sessionId)
        if (result.authRequired) {
          localStorage.removeItem(`easyeditor_session_${provider}`)
          this.providerHasSession[provider] = false
        } else if (result.failed.length > 0) {
          this.syncFailed[provider] = true
        } else {
          this.syncFailed[provider] = false
          totalSynced += result.synced.length
        }
      }
      await this.refreshPendingCount()
      if (totalSynced > 0) {
        this.syncMessage = `${totalSynced} visit(s) synced.`
        setTimeout(() => { this.syncMessage = '' }, 5000)
      }
    },
    
    async loadEnvironmentConfig() {
      try {
        console.log('🔧 Loading environment configuration...')
        const response = await fetch('/api/environment')
        
        if (response.ok) {
          this.environmentConfig = await response.json()
          this.environment = this.environmentConfig.environment
          this.isProduction = this.environmentConfig.is_production
          document.title = this.appTitle
          
          console.log('✅ Environment loaded:', {
            environment: this.environment,
            isProduction: this.isProduction,
            featureService: this.environmentConfig.feature_service_base
          })
        } else {
          console.warn('⚠️ Could not load environment config, using safe defaults')
          this.environment = 'dev'
          this.isProduction = false
        }
      } catch (error) {
        console.warn('⚠️ Could not load environment config (offline?), using safe defaults')
        this.environment = 'dev'
        this.isProduction = false
      }
    }
  }
}
</script>

<style scoped>
.app-main-card {
  position: relative;
}

.version-footer {
  text-align: right;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 0.5rem;
}

.version-footer a {
  color: inherit;
  text-decoration: underline;
}

.app-card-heading-row {
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
}

.app-card-logo {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  object-fit: contain;
  pointer-events: none;
}

.app-card-header {
  flex: 1;
  min-width: 0;
  padding-left: 0;
  padding-right: 0;
}

/* Tight line box on primary headline so cap height sits closer to logo top */
.app-card-heading-row .app-card-header > .text-3xl:first-child,
.app-card-heading-row .app-card-header > .cams-title-block > .text-3xl:first-child {
  line-height: 1.1;
}

.cams-title-block {
  word-break: break-word;
}

.cams-title-location {
  overflow-wrap: anywhere;
}

.p-error {
  border: 1px solid red;
}
.p-error span:not(.status-sym) {
  color: red;
  font-size: 0.8rem;
}

.inline-flex {
  display: inline-flex;
  align-items: center;
}

.inline-flex > div {
  margin-right: 1rem;
}


.loading-container {
  padding: 2rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.loading-spinner {
  display: inline-block;
  width: 32px;
  height: 32px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  padding: 2rem;
  background: rgba(254, 242, 242, 0.9);
  border: 2px solid #fca5a5;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(239, 68, 68, 0.1);
}

.retry-button {
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

.retry-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

/* Map-aligned status symbols (Option A: Unicode + single color) */
.status-sym {
  display: inline-block;
  font-size: 0.95em;
  line-height: 1;
  vertical-align: middle;
}
.status-sym-red {
  color: #ff0000;
  text-shadow:
    -1px 0 0 #e60000,
    1px 0 0 #e60000,
    0 -1px 0 #e60000,
    0 1px 0 #e60000,
    -1px -1px 0 #e60000,
    1px -1px 0 #e60000,
    -1px 1px 0 #e60000,
    1px 1px 0 #e60000;
}
.status-sym-purple {
  color: #c500ff;
  text-shadow:
    -1px 0 0 #c500ff,
    1px 0 0 #c500ff,
    0 -1px 0 #c500ff,
    0 1px 0 #c500ff,
    -1px -1px 0 #c500ff,
    1px -1px 0 #c500ff,
    -1px 1px 0 #c500ff,
    1px 1px 0 #c500ff;
}
.status-sym-orange {
  color: #e69800;
  text-shadow:
    -1px 0 0 #e69800,
    1px 0 0 #e69800,
    0 -1px 0 #e69800,
    0 1px 0 #e69800,
    -1px -1px 0 #e69800,
    1px -1px 0 #e69800,
    -1px 1px 0 #e69800,
    1px 1px 0 #e69800;
}
.status-sym-dot {
  border-radius: 50%;
  box-sizing: border-box;
  width: 1em;
  height: 1em;
  min-width: 1em;
  min-height: 1em;
  text-shadow: none;
  color: transparent;
  vertical-align: -0.14em;
  line-height: 0;
}
.status-sym-red.status-sym-dot {
  background-color: #ff0000;
  border: 2px solid #e60000;
}
.status-sym-orange.status-sym-dot {
  background-color: #e69800;
  border: 2px solid #e69800;
}
.status-sym-diamond {
  box-sizing: border-box;
  width: 0.78em;
  height: 0.78em;
  border: 2px solid;
  transform: rotate(45deg);
  transform-origin: center;
  vertical-align: middle;
  position: relative;
  top: -0.06em;
  margin: 0 0 0 0.55em;
  text-shadow: none;
  color: transparent;
  line-height: 0;
  flex-shrink: 0;
}
.status-sym-yellow.status-sym-diamond {
  background-color: #ffff00;
  border-color: #bdbd0d;
}
.status-sym-pink.status-sym-diamond {
  background-color: #ffbebe;
  border-color: #ff7f7f;
}
.status-sym-green.status-sym-diamond {
  background-color: #55ff00;
  border-color: #38a800;
}
.status-sym-square {
  border-radius: 0;
  box-sizing: border-box;
  width: 1em;
  height: 1em;
  min-width: 1em;
  min-height: 1em;
  border: 2px solid;
  text-shadow: none;
  color: transparent;
  vertical-align: -0.14em;
  line-height: 0;
}
.status-sym-grey.status-sym-square {
  background-color: #d6d6d6;
  border-color: #999999;
}
.status-sym-black {
  color: #242424;
  text-shadow:
    -1px 0 0 #1a1a1a,
    1px 0 0 #1a1a1a,
    0 -1px 0 #1a1a1a,
    0 1px 0 #1a1a1a,
    -1px -1px 0 #1a1a1a,
    1px -1px 0 #1a1a1a,
    -1px 1px 0 #1a1a1a,
    1px 1px 0 #1a1a1a;
}

.offline-banner {
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  text-align: center;
  color: #92400e;
  font-weight: 500;
}

.pending-count {
  background: #f59e0b;
  color: #fff;
  border-radius: 12px;
  padding: 0.1rem 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
}

.sync-message {
  background: #d1fae5;
  border: 1px solid #34d399;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  text-align: center;
  color: #065f46;
  font-weight: 500;
}

.close-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.close-btn {
  padding: 0.5rem 1.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #f9fafb;
  color: #374151;
  cursor: pointer;
  font-size: 0.875rem;
}

.close-btn:hover {
  background: #f3f4f6;
}

.offline-landing {
  padding: 1rem 0;
}

.offline-landing-section-header {
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.offline-landing-section-header.is-online {
  color: #065f46;
}

.offline-landing-section-header.is-offline {
  color: #92400e;
}

.offline-landing-message {
  font-size: 0.9rem;
  color: #4b5563;
  margin-bottom: 1rem;
  line-height: 1.6;
}

.offline-landing-sync-status {
  margin-bottom: 1rem;
}

.offline-banner-inline {
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  color: #92400e;
  font-weight: 500;
  font-size: 0.9rem;
}


.sync-pending-inline {
  background: #eff6ff;
  border: 1px solid #93c5fd;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  color: #1d4ed8;
  font-weight: 500;
  font-size: 0.9rem;
}

.sync-ok-inline {
  background: #d1fae5;
  border: 1px solid #34d399;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  color: #065f46;
  font-weight: 500;
  font-size: 0.9rem;
}

.offline-landing-note {
  font-size: 0.85rem;
  color: #6b7280;
  font-style: italic;
  line-height: 1.6;
  margin-top: 0.75rem;
}
</style>